import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import MaterialTable, { MTableToolbar } from '@material-table/core';
import flowRight from 'lodash.flowright';
import { injectIntl } from 'react-intl';
import { withStyles } from '@material-ui/styles';

import FilterButton from '../../../partials/tableCustomButtons/FilterButton';
import tableColumns from './tableColumns';
import useLocalTableStore from '../../../utils/useLocalTableStore';
import customTableIcons from '../../../partials/tableIcons/customTableIcons';
import { mapTableOptions } from '../../../consts/tableOptions';
import useTableLocalization from '../../../utils/useTableLocalization';
import { actions as reduxActions } from '../reducers';
import GroupUsers from './GroupUsers';
import filterCustomJobs from '../../../utils/filterCustomJobs';
import { doesArrayHasLength } from '../../../utils/isDefined';
import CustomJobsMenu from '../../../partials/customJobsMenu/CustomJobsMenu';
import { fetchGroups } from '../actions/getGroups';
import RefreshButton from '../../../partials/tableCustomButtons/RefreshButton';
import GroupsTableTitle from './GroupsTableTitle';
import styles from '../styles';

const GroupsTable = ({
  intl,
  isLoading,
  columns,
  setColumns,
  groups,
  customJobs,
  fetchGroups,
  connectClientId,
  classes
}) => {
  const [filtering, setFiltering] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const { localization, language } = useTableLocalization();

  useEffect(() => {
    fetchGroups(connectClientId);
    // eslint-disable-next-line
  }, [connectClientId]);

  useEffect(() => {
    if (refresh) {
      fetchGroups(connectClientId);
      setRefresh(false);
    }
    // eslint-disable-next-line
  }, [refresh]);

  const tableDefaultColumns = useMemo(() => {
    return tableColumns();
  }, []);

  const {
    pageSize,
    onChangeRowsPerPage,
    onChangeColumnHidden
  } = useLocalTableStore({
    tableName: 'Groups',
    setColumns,
    tableDefaultColumns,
    language
  });

  const mappedJobs = useMemo(() => {
    return filterCustomJobs(customJobs, 'Groups');
  }, [customJobs]);

  const actions = useMemo(
    () => {
      let buttons = [
        {
          icon: () => <FilterButton />,
          onClick: () => {
            setFiltering(!filtering);
          },
          isFreeAction: true,
          tooltip: intl.formatMessage({ id: 'BUTTON.FILTER' })
        }
      ];

      if (doesArrayHasLength(mappedJobs)) {
        buttons = [
          ...buttons,
          {
            icon: () => <CustomJobsMenu customJobs={mappedJobs} />,
            onClick: () => {},
            isFreeAction: true
          }
        ];
      }

      buttons = [
        ...buttons,
        {
          icon: () => <RefreshButton tableHeader={true} />,
          onClick: () => setRefresh(true),
          isFreeAction: true
        }
      ];

      return buttons;
    },
    // eslint-disable-next-line
    [filtering, customJobs]
  );

  const options = useMemo(() => {
    return {
      ...mapTableOptions({ exportData: false }),
      selection: false,
      filtering,
      pageSize
    };
  }, [filtering, pageSize]);

  const detailPanel = useCallback(({ rowData }) => {
    return <GroupUsers name={rowData.name} />;
  }, []);

  return (
    <MaterialTable
      components={{
        Toolbar: props => (
          <MTableToolbar
            classes={{
              title: classes.toolbarTitle
            }}
            {...props}
          />
        )
      }}
      title={<GroupsTableTitle isLoading={isLoading} />}
      icons={customTableIcons}
      actions={actions}
      columns={columns}
      data={groups}
      options={options}
      localization={localization}
      detailPanel={detailPanel}
      onChangeColumnHidden={onChangeColumnHidden}
      onRowsPerPageChange={onChangeRowsPerPage}
    />
  );
};

const mapStatesToProps = ({ groups, loader, auth, selectClient }) => {
  return {
    ...groups,
    isLoading: loader.isSpinVisible,
    customJobs: auth.customJobs,
    connectClientId: selectClient.selectedClient.connect30_domain
  };
};

const mapDispatchToProps = {
  setColumns: reduxActions.setColumns,
  fetchGroups
};

export default flowRight(
  injectIntl,
  connect(mapStatesToProps, mapDispatchToProps),
  withStyles(styles)
)(GroupsTable);
